import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetOrganization,
  getAllOrganizations,
  editOrganization as editOrganizationApi,
  deleteOrganization as deleteOrganizationApi,
} from 'src/api/organization';
import { useAuthContext } from 'src/auth/hooks';
import { useSnackbar } from 'src/components/snackbar';
import { Organization } from 'src/models/organization';
import { Role } from 'src/models/user';
import { OrganizationContext } from './organization-context-types';

interface OrganizationProviderProps {
  children: React.ReactNode;
}

const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<string | null>(null);

  const fetchAllOrganizations = useCallback(async () => {
    const res = await getAllOrganizations();
    if (!res.error) {
      setOrganizations(res.organizations);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchAllOrganizations();
      if (user?.role !== Role.SUPER_ADMIN) {
        setCurrentOrg(user?.organization ?? null);
      } else {
        setCurrentOrg(null);
      }
    }
    const org = localStorage.getItem('currentOrg');
    if (org && org !== null) {
      setCurrentOrg(org);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllOrganizations, user?._id]);

  const fetchOrganizationById = async (organizationId: string): Promise<Organization | null> => {
    const res = await GetOrganization(organizationId);
    if (!res.error) {
      return res.organization[0] || null;
    }
    return null;
  };

  const deleteOrganization = useCallback(
    async (organizationId: string): Promise<boolean> => {
      try {
        const res = await deleteOrganizationApi(organizationId);
        if (res.success) {
          setOrganizations((prevOrganizations) =>
            prevOrganizations.filter((d) => d._id !== organizationId)
          );
          enqueueSnackbar('Organization deleted', { variant: 'success' });
          return true;
        }
        // Backend returns 200 with success:false (e.g. org still has users) —
        // surface the reason so the admin knows why it wasn't deleted.
        enqueueSnackbar(res.error ?? 'Failed to delete organization', { variant: 'error' });
        return false;
      } catch (error) {
        enqueueSnackbar(
          (error as { error?: string })?.error ?? 'Failed to delete organization',
          { variant: 'error' }
        );
        return false;
      }
    },
    [enqueueSnackbar]
  );

  const editOrganization = useCallback(
    (organizationData: Partial<Organization> & { _id: string }): void => {
      editOrganizationApi(organizationData).then((res) => {
        if (res.success && res.data) {
          const updatedOrganization = res.data;
          const updatedOrganizations = (organizations ?? []).map((org) =>
            org._id === updatedOrganization._id ? updatedOrganization : org
          );
          setOrganizations(updatedOrganizations);
        }
      });
    },
    [organizations]
  );

  const handleOrganizationSwitchAndExit = useCallback((organizationId: string | null) => {
    setCurrentOrg(organizationId);
    if (organizationId !== null) {
      localStorage.setItem('currentOrg', organizationId);
    } else {
      localStorage.removeItem('currentOrg');
    }
  }, []);

  const providerValue = useMemo(
    () => ({
      organizations,
      fetchOrganizationById,
      fetchAllOrganizations,
      deleteOrganization,
      editOrganization,
      currentOrg,
      setCurrentOrg,
      handleOrganizationSwitchAndExit,
    }),
    [organizations, fetchAllOrganizations, currentOrg, deleteOrganization, editOrganization, handleOrganizationSwitchAndExit]
  );

  return (
    <OrganizationContext.Provider value={providerValue}>{children}</OrganizationContext.Provider>
  );
};

export default OrganizationProvider;
